var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Divider, MenuItem, TextField, Typography, } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import React from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCreateIntraSquadTournament } from "../api/TournamentApiProvider";
import { useRetrievePlayersByTeamId, useRetrieveTeamPreferences, useRetrieveUserTeamPermissions, } from "../ApiProvider";
import { TeamChooser, TOURNAMENT_TEAM_COLOR_BLUE, TOURNAMENT_TEAM_COLOR_RED, TOURNAMENT_TEAM_COLOR_WHITE, } from "../matches/TeamChooser";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { TournamentType } from "./TournamentType";
var halfLengths = Array.from({ length: 9 }, function (_, i) { return (i + 1) * 5; });
var halfTimeLengths = __spreadArray([
    0,
    2
], __read(Array.from({ length: 3 }, function (_, i) { return (i + 1) * 5; })), false);
var breakLengths = Array.from({ length: 12 }, function (_, i) { return (i + 1) * 5; });
export var IntraSquadTournamentForm = function (_a) {
    var team = _a.team;
    var navigate = useNavigate();
    // Check permissions
    var _b = useRetrieveUserTeamPermissions({ teamId: team === null || team === void 0 ? void 0 : team.id }), userTeamPermissions = _b.data, userTeamPermissionsIsLoading = _b.isLoading, userTeamPermissionsError = _b.error;
    var _c = (userTeamPermissions || {}).canModify, canModify = _c === void 0 ? false : _c;
    var mutation = useCreateIntraSquadTournament({
        team: team,
        onMutate: function () { },
        onSuccess: function (resp) {
            navigate("/teams/".concat(team.id, "/intra-squad-tournaments/").concat(resp.data.id));
        },
        onError: function () { },
    });
    var _d = useRetrieveTeamPreferences({ teamId: team.id }), teamPreferencesIsLoading = _d.isLoading, teamPreferencesError = _d.error, teamPreference = _d.data;
    var _e = useRetrievePlayersByTeamId({ teamId: team.id }), players = _e.data, playersLoading = _e.isLoading, playersError = _e.isError;
    var _f = __read(React.useState({
        teams: [],
        bench: [],
    }), 2), teamSelection = _f[0], _setTeamSelection = _f[1];
    var setTeamSelection = function (newTeamSelection) {
        _setTeamSelection(newTeamSelection);
        setValue("intraSquadTournamentTeams", newTeamSelection.teams.map(function (team) { return ({
            name: team.name,
            players: team.players.map(function (p) { return p.id; }),
            primaryColour: team.primaryColour,
        }); }));
    };
    var useFormProps = useForm({
        defaultValues: {
            tournamentName: "",
            tournamentType: TournamentType.RoundRobin,
            startTime: undefined,
            halfLength: 45,
            halfTimeLength: 15,
            breakLength: 5,
        },
    });
    React.useEffect(function () {
        if (players) {
            setTeamSelection({
                teams: [
                    {
                        name: "".concat(TOURNAMENT_TEAM_COLOR_RED.name, " Team"),
                        players: [],
                        primaryColour: TOURNAMENT_TEAM_COLOR_RED.hex,
                    },
                    {
                        name: "".concat(TOURNAMENT_TEAM_COLOR_BLUE.name, " Team"),
                        players: [],
                        primaryColour: TOURNAMENT_TEAM_COLOR_BLUE.hex,
                    },
                    {
                        name: "".concat(TOURNAMENT_TEAM_COLOR_WHITE.name, " Team"),
                        players: [],
                        primaryColour: TOURNAMENT_TEAM_COLOR_WHITE.hex,
                    },
                ],
                bench: players.filter(function (p) { return p.isActive !== false; }) || [],
            });
        }
    }, [players]);
    var control = useFormProps.control, handleSubmit = useFormProps.handleSubmit, setValue = useFormProps.setValue, errors = useFormProps.formState.errors;
    var onSubmit = function (data) {
        mutation.mutate(data);
    };
    var loading = userTeamPermissionsIsLoading ||
        teamPreferencesIsLoading ||
        playersLoading ||
        mutation.isLoading;
    var error = userTeamPermissionsError || playersError || teamPreferencesError;
    if (loading) {
        return _jsx(Loading, {});
    }
    if (error) {
        return _jsx(ErrorView, { error: error });
    }
    if (!canModify) {
        return _jsx(Forbidden, {});
    }
    if (!teamPreference || !team) {
        return _jsx(ErrorView, { error: new Error("Unable to load data!") });
    }
    return (_jsxs(FormProvider, __assign({}, useFormProps, { children: [_jsx(ErrorSnackbar, { open: !!mutation.error, message: "Failed to Create tournament! Please try again." }), _jsxs(Box, { component: "form", onSubmit: handleSubmit(onSubmit), noValidate: true, role: "form", sx: { mt: 1 }, children: [_jsxs(Typography, { variant: "h4", gutterBottom: true, sx: { fontSize: { xs: "1.75rem", sm: "2.125rem" } }, children: ["Create Intra-Squad Tournament for ", team.name] }), _jsxs(Box, { mt: 3, mb: 2, sx: { mb: 4 }, children: [_jsx(Typography, { variant: "h6", children: "Assign Players to Teams" }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Drag and drop players into teams. Remaining players will stay on the bench." }), _jsx(Divider, { sx: { mt: 2 } })] }), _jsx(TeamChooser, { teamSelection: teamSelection, setTeamSelection: setTeamSelection, canEditTeamNames: true, minTeams: 3, maxTeams: 8 }), _jsx(Divider, { sx: { mt: 2 } }), _jsxs(Box, { mt: 4, mb: 2, children: [_jsx(Typography, { variant: "h6", children: "Set Match Rules & Details" }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Define tournament structure, timing, and kick-off." }), _jsx(Divider, { sx: { mt: 2 } })] }), _jsx(Controller, { name: "tournamentName", control: control, defaultValue: "", rules: { required: "Tournament Name is required" }, render: function (_a) {
                            var _b;
                            var field = _a.field;
                            return (_jsx(TextField, __assign({}, field, { margin: "normal", fullWidth: true, label: "Tournament Name", error: !!errors.tournamentName, helperText: (_b = errors.tournamentName) === null || _b === void 0 ? void 0 : _b.message })));
                        } }), _jsx(Controller, { name: "startTime", control: control, rules: { required: "Start Time is required" }, render: function (_a) {
                            var field = _a.field;
                            return (_jsx(DateTimePicker, __assign({ label: "Start Time" }, field, { value: field.value ? dayjs(field.value) : null, onChange: function (newValue) { return field.onChange(newValue); }, sx: { width: "100%" }, slotProps: {
                                    textField: {
                                        error: !!errors.startTime,
                                        helperText: errors.startTime ? errors.startTime.message : "",
                                    },
                                } })));
                        } }), _jsx(Controller, { control: control, name: "tournamentType", rules: { required: "Tournament type is required" }, render: function (_a) {
                            var field = _a.field;
                            return (_jsx(TextField, __assign({}, field, { required: true, select: true, fullWidth: true, margin: "normal", label: "Tournament Type", children: _jsx(MenuItem, { value: TournamentType.RoundRobin, children: "Round Robin" }) })));
                        } }), _jsx(Controller, { control: control, name: "halfLength", rules: {
                            required: "Half Length is required",
                            min: { value: 1, message: "Half Length must be at least 1" },
                            max: { value: 60, message: "Half Length must be 60 or less" },
                        }, render: function (_a) {
                            var _b;
                            var field = _a.field;
                            return (_jsx(TextField, __assign({}, field, { select: true, fullWidth: true, margin: "normal", label: "Half Length (mins)", error: !!errors.halfLength, helperText: ((_b = errors.halfLength) === null || _b === void 0 ? void 0 : _b.message) ||
                                    "Length of a half. For single-half games, set the full game length here.", children: halfLengths.map(function (v) { return (_jsx(MenuItem, { value: v, children: v }, v)); }) })));
                        } }), _jsx(Controller, { control: control, name: "halfTimeLength", rules: {
                            min: { value: 0, message: "Half-Time must be 0 or more" },
                            max: { value: 15, message: "Half-Time must be 15 or less" },
                        }, render: function (_a) {
                            var _b;
                            var field = _a.field;
                            return (_jsx(TextField, __assign({}, field, { select: true, fullWidth: true, margin: "normal", label: "Half-Time Length (mins)", error: !!errors.halfTimeLength, helperText: ((_b = errors.halfTimeLength) === null || _b === void 0 ? void 0 : _b.message) ||
                                    "Length of the break between halves. Set to 0 for single-half games.", children: halfTimeLengths.map(function (v) { return (_jsx(MenuItem, { value: v, children: v }, v)); }) })));
                        } }), _jsx(Controller, { control: control, name: "breakLength", rules: {
                            min: { value: 0, message: "Break must be 0 or more" },
                            max: { value: 60, message: "Break must be 60 or less" },
                        }, render: function (_a) {
                            var _b;
                            var field = _a.field;
                            return (_jsx(TextField, __assign({}, field, { select: true, fullWidth: true, margin: "normal", label: "Break Length (mins)", error: !!errors.breakLength, helperText: ((_b = errors.breakLength) === null || _b === void 0 ? void 0 : _b.message) || "Break time between matches.", children: breakLengths.map(function (v) { return (_jsx(MenuItem, { value: v, children: v }, v)); }) })));
                        } }), _jsx(Button, { type: "submit", fullWidth: true, variant: "contained", sx: { mt: 3, mb: 2 }, children: "Create" })] })] })));
};
